import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { withAuthRecovery } from '@/lib/authRecovery';
import { buildWebInviteLink } from '@/lib/inviteLinks';
import {
  createSupabaseRequestHeaders,
  getSupabaseCredentials,
} from '@/lib/supabaseCredentials';
import { 
  ManagementCompanyInvite, 
  ManagementCompanyUser
} from '@/types';
import type { TerritorialJobTitle } from '@/types';

const accessLevelRequests = new Map<string, Promise<'standard' | 'advanced' | null>>();
const jobTitleRequests = new Map<string, Promise<TerritorialJobTitle>>();

interface CreateInviteRequest {
  access_level: 'standard' | 'advanced';
  max_uses?: number;
  valid_days?: number;
  description?: string;
  target_email?: string;
  target_first_name?: string;
  target_last_name?: string;
  target_position?: string;
}

interface CreateInviteResponse {
  invite_id: number;
  invite_code: string;
  created_at: string;
  valid_until: string;
}

interface UseInviteResponse {
  success: boolean;
  message: string;
  access_level?: string;
}

export const useManagementCompanySimple = (userId?: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const getAccessLevelCacheKey = useCallback(
    () => (userId ? `mc_access_level_${userId}` : null),
    [userId]
  );
  const clearError = useCallback(() => setError(null), []);

  // Создание инвайта
  const createInvite = useCallback(async (
    data: CreateInviteRequest
  ): Promise<CreateInviteResponse | null> => {
    if (!userId) {
      setError('Пользователь не авторизован');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: result, error: dbError } = await withAuthRecovery(() =>
        (supabase as any).rpc('create_management_company_invite_v2', {
          p_user_id: userId,
          p_access_level: data.access_level,
          p_max_uses: data.max_uses || 1,
          p_valid_days: data.valid_days || 7,
          p_description: data.description || null,
          p_target_email: data.target_email || null,
          p_target_first_name: data.target_first_name || null,
          p_target_last_name: data.target_last_name || null,
          p_target_position: data.target_position || null
        })
      );

      if (dbError) {
        console.error('Error creating invite:', dbError);
        setError('Ошибка создания инвайта');
        return null;
      }

      return result?.[0] || null;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Получение списка инвайтов
  const getInvites = useCallback(async (): Promise<ManagementCompanyInvite[]> => {
    if (!userId) {
      setError('Пользователь не авторизован');
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: dbError } = await withAuthRecovery(() =>
        (supabase as any).rpc('get_management_company_invites_v2', {
          p_user_id: userId
        })
      );

      if (dbError) {
        console.error('Error fetching invites:', dbError);
        setError('Ошибка загрузки инвайтов');
        return [];
      }

      return data || [];
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка';
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Деактивация инвайта
  const deactivateInvite = useCallback(async (inviteId: number): Promise<boolean> => {
    if (!userId) {
      setError('Пользователь не авторизован');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: dbError } = await withAuthRecovery(() =>
        (supabase as any).rpc('deactivate_management_company_invite_v2', {
          p_invite_id: inviteId,
          p_user_id: userId
        })
      );

      if (dbError) {
        console.error('Error deactivating invite:', dbError);
        setError('Ошибка деактивации инвайта');
        return false;
      }

      const result = data?.[0];
      if (!result?.success) {
        setError(result?.message || 'Ошибка деактивации инвайта');
        return false;
      }

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Получение списка пользователей УК
  const getUsers = useCallback(async (): Promise<ManagementCompanyUser[]> => {
    if (!userId) {
      setError('Пользователь не авторизован');
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: dbError } = await withAuthRecovery(() =>
        (supabase as any).rpc('get_management_company_users_v2', {
          p_user_id: userId
        })
      );

      if (dbError) {
        console.error('Error fetching users:', dbError);
        setError('Ошибка загрузки пользователей');
        return [];
      }

      return data || [];
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка';
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Использование инвайта (для бота)
  const useInvite = useCallback(async (
    inviteCode: string
  ): Promise<UseInviteResponse> => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('Нет токена авторизации');
      }

      const { url: supabaseUrl, apiKey } = getSupabaseCredentials();
      if (!supabaseUrl || !apiKey) {
        throw new Error('Не настроено подключение к Supabase');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/use-management-invite`, {
        method: 'POST',
        headers: createSupabaseRequestHeaders(apiKey, token, {
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          invite_code: inviteCode
        })
      });

      const result: UseInviteResponse = await response.json();
      
      if (!result.success) {
        setError(result.message);
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка сети';
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // Генерация ссылки на инвайт
  const generateInviteLink = useCallback((inviteCode: string): string => {
    return buildWebInviteLink(inviteCode);
  }, []);

  // Получение уровня доступа к УК
  const getAccessLevel = useCallback(async (): Promise<'standard' | 'advanced' | null> => {
    if (!userId) {
      console.log('❌ No userId provided');
      return null;
    }

    const cacheKey = getAccessLevelCacheKey();
    if (cacheKey) {
      const cached = localStorage.getItem(cacheKey);
      if (cached === 'standard' || cached === 'advanced') {
        console.log('♻️ Using cached access level:', cached);
        return cached;
      }
    }

    const inFlightKey = userId;
    const existingRequest = accessLevelRequests.get(inFlightKey);
    if (existingRequest) {
      console.log('♻️ Reusing in-flight access level request for user_id:', userId);
      return existingRequest;
    }

    console.log('🔍 Checking access level for user_id:', userId);
    const request = (async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error } = await withAuthRecovery(() =>
          supabase
            .from('management_company_access')
            .select('access_level')
            .eq('user_id', userId)
            .eq('is_active', true)
            .single()
        );

        console.log('📊 Access level check result:', { data, error });

        if (error) {
          if (error.code === 'PGRST116') {
            console.log('❌ No management company access found');
            if (cacheKey) {
              localStorage.removeItem(cacheKey);
            }
            return null;
          }
          console.error('❌ Error checking access level:', error);
          if (cacheKey) {
            const cached = localStorage.getItem(cacheKey);
            if (cached === 'standard' || cached === 'advanced') {
              console.log('♻️ Using cached access level after access check error:', cached);
              return cached;
            }
          }
          return null;
        }

        if (!data) {
          console.log('❌ No data returned');
          return null;
        }

        const accessLevel = (data as { access_level: 'standard' | 'advanced' }).access_level;
        console.log('✅ Access level:', accessLevel);
        if (cacheKey) {
          localStorage.setItem(cacheKey, accessLevel);
        }

        return accessLevel;
      } catch (err) {
        console.error('❌ Exception checking access level:', err);
        if (cacheKey) {
          const cached = localStorage.getItem(cacheKey);
          if (cached === 'standard' || cached === 'advanced') {
            console.log('♻️ Using cached access level after exception:', cached);
            return cached;
          }
        }
        return null;
      } finally {
        setLoading(false);
        accessLevelRequests.delete(inFlightKey);
        console.log('🏁 Access level check completed');
      }
    })();

    accessLevelRequests.set(inFlightKey, request);
    return request;
  }, [userId, getAccessLevelCacheKey]);

  const getJobTitle = useCallback(async (): Promise<TerritorialJobTitle> => {
    if (!userId) return null;

    const existingRequest = jobTitleRequests.get(userId);
    if (existingRequest) return existingRequest;

    const request = (async () => {
      try {
        const { data, error } = await withAuthRecovery(() =>
          supabase
            .from('management_company_access')
            .select('job_title')
            .eq('user_id', userId)
            .eq('is_active', true)
            .single()
        );

        if (error || !data) return null;

        return (data as { job_title: TerritorialJobTitle }).job_title;
      } catch (err) {
        console.error('Error checking management company job title:', err);
        return null;
      } finally {
        jobTitleRequests.delete(userId);
      }
    })();

    jobTitleRequests.set(userId, request);
    return request;
  }, [userId]);

  // Проверка доступа к управлению УК (только advanced)
  const checkManagementAccess = useCallback(async (): Promise<boolean> => {
    const accessLevel = await getAccessLevel();
    return accessLevel === 'advanced';
  }, [getAccessLevel]);

  return {
    // State
    loading,
    error,
    
    // Actions
    createInvite,
    getInvites,
    deactivateInvite,
    getUsers,
    useInvite,
    generateInviteLink,
    getAccessLevel,
    getJobTitle,
    checkManagementAccess,
    clearError
  };
};
